import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import { Layers, Eye, Edit } from 'lucide-react';
import type { Block } from '../../types/orchard';

interface BlockCardProps {
  block: Block;
  showFarmName?: boolean;
}

function BlockCard({ block, showFarmName = false }: BlockCardProps) {
  const navigate = useNavigate();

  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-neutral-900 truncate">{block.name}</h3>
            {showFarmName && block.farm_name && (
              <p className="text-xs text-neutral-500 truncate mt-0.5">Farm: {block.farm_name}</p>
            )}
          </div>
          <div className="flex-shrink-0 ml-3">
            <div className="h-9 w-9 rounded-lg bg-primary-100 flex items-center justify-center">
              <Layers className="h-4 w-4 text-primary-600" />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-neutral-50 rounded-lg p-2.5 text-center">
            <p className="text-base font-bold text-neutral-900">
              {block.area_hectares != null ? Number(block.area_hectares).toFixed(2) : '—'}
            </p>
            <p className="text-xs text-neutral-500">Hectares</p>
          </div>
          <div className="bg-success-50 rounded-lg p-2.5 text-center">
            <p className="text-base font-bold text-success-700">{block.cultivar_area_count ?? 0}</p>
            <p className="text-xs text-success-600">Cultivars</p>
          </div>
        </div>

        {block.description && (
          <p className="text-xs text-neutral-600 line-clamp-2 mb-3">{block.description}</p>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-neutral-100">
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(`/orchard/blocks/${block.id}`)}
            className="flex-1"
          >
            <Eye className="h-3.5 w-3.5" />
            View Block
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/orchard/blocks/${block.id}/edit`)}
          >
            <Edit className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default BlockCard;
